import React from "react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

// Função para formatar valores em BRL
const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
    }).format(value || 0);
};

// Componente SummaryCards
const SummaryCards = ({ totals, month, day, paymentTypes, showValues }) => {
    const currentDate = new Date();

    const currentMonth = month
        ? format(parseISO(month), "MMMM", { locale: ptBR })
        : format(currentDate, "MMMM", { locale: ptBR });

    const currentYear = month
        ? format(parseISO(`${month}-01`), "yyyy")
        : format(currentDate, "yyyy");

    const formattedDay = day
        ? format(parseISO(day), "dd/MM/yyyy")
        : format(currentDate, "dd/MM/yyyy");

    // Estilo para aplicar o blur condicionalmente
    const blurStyle = showValues ? {} : { filter: "blur(5px)" };

    // Função para determinar a cor do saldo (verde para positivo, vermelho para negativo)
    const getBalanceColor = (balance) => {
        return balance >= 0 ? "#2ecc71" : "#e74c3c";
    };

    return (
        <div
            style={{
                display: "grid",
                // Força 3 colunas em telas grandes e 2 ou 1 em telas menores
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", // Ajustado minmax para permitir 3 cards por linha, um pouco mais estreitos
                gap: "12px", // Espaçamento entre os cards um pouco menor
                marginBottom: "15px", // Margem inferior um pouco menor
            }}
        >
            {/* Card 1: Resumo por Período (Entradas, Saídas e Saldo) - MAIS COMPACTO */}
            <div style={cardStyle}>
                <h3 style={titleStyle}>Resumo por Período</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    {" "}
                    {/* Usa flexbox para empilhar seções */}
                    {/* Seção Diária */}
                    <div style={periodSectionStyle}>
                        <h4 style={labelStyle}>Diário ({formattedDay})</h4>
                        <div style={compactValueRow}>
                            <p style={{ ...valueStyleMicro, ...blurStyle, color: "#2ecc71" }}>
                                {" "}
                                {formatCurrency(totals?.dailyIncome)}
                            </p>
                            <p style={{ ...valueStyleMicro, ...blurStyle, color: "#e74c3c" }}>
                                {" "}
                                {formatCurrency(totals?.dailyExpense)}
                            </p>
                            <p
                                style={{
                                    ...valueStyleMicro,
                                    ...blurStyle,
                                    fontWeight: "bold",
                                    color: getBalanceColor(totals?.dailyBalance),
                                }}
                            >
                                Saldo: {formatCurrency(totals?.dailyBalance)}
                            </p>
                        </div>
                    </div>
                    {/* Seção Mensal */}
                    <div style={periodSectionStyle}>
                        <h4 style={labelStyle}>Mensal ({currentMonth})</h4>
                        <div style={compactValueRow}>
                            <p style={{ ...valueStyleMicro, ...blurStyle, color: "#2ecc71" }}>
                                {" "}
                                {formatCurrency(totals?.monthlyIncome)}
                            </p>
                            <p style={{ ...valueStyleMicro, ...blurStyle, color: "#e74c3c" }}>
                                {" "}
                                {formatCurrency(totals?.monthlyExpense)}
                            </p>
                            <p
                                style={{
                                    ...valueStyleMicro,
                                    ...blurStyle,
                                    fontWeight: "bold",
                                    color: getBalanceColor(totals?.monthlyBalance),
                                }}
                            >
                                Saldo: {formatCurrency(totals?.monthlyBalance)}
                            </p>
                        </div>
                    </div>
                    {/* Seção Anual */}
                    <div style={periodSectionStyle}>
                        <h4 style={labelStyle}>Anual ({currentYear})</h4>
                        <div style={compactValueRow}>
                            <p style={{ ...valueStyleMicro, ...blurStyle, color: "#2ecc71" }}>
                                {" "}
                                {formatCurrency(totals?.yearlyIncome)}
                            </p>
                            <p style={{ ...valueStyleMicro, ...blurStyle, color: "#e74c3c" }}>
                                {" "}
                                {formatCurrency(totals?.yearlyExpense)}
                            </p>
                            <p
                                style={{
                                    ...valueStyleMicro,
                                    ...blurStyle,
                                    fontWeight: "bold",
                                    color: getBalanceColor(totals?.yearlyBalance),
                                }}
                            >
                                Saldo: {formatCurrency(totals?.yearlyBalance)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Card 2: Totais por Forma de Pagamento (Diário - Entradas E Saídas) - MAIS COMPACTO */}
            <div style={cardStyle}>
                <h3 style={titleStyle}>Pagamentos ({formattedDay})</h3>
                <div style={paymentSectionScroll}>
                    {" "}
                    {/* Adicionado para scroll vertical se necessário */}
                    {paymentTypes &&
                        paymentTypes.filter((type) => type !== "Todos").length > 0 ? (
                        paymentTypes
                            .filter((type) => type !== "Todos")
                            .map((type) => (
                                <div key={type} style={paymentRowStyleCompact}>
                                    <span style={textStyleCompact}>{type}:</span>
                                    <div style={paymentValueGroup}>
                                        <span
                                            style={{
                                                ...valueStyleMicro,
                                                ...blurStyle,
                                                color: "#2ecc71",
                                            }}
                                        >
                                            {formatCurrency(totals?.paymentMethodsDailyIncome[type])}
                                        </span>
                                        <span
                                            style={{
                                                ...valueStyleMicro,
                                                ...blurStyle,
                                                color: "#e74c3c",
                                            }}
                                        >
                                            {formatCurrency(totals?.paymentMethodsDailyExpense[type])}
                                        </span>
                                    </div>
                                </div>
                            ))
                    ) : (
                        <p style={emptyTextStyle}>Nenhum dado disponível para este dia.</p>
                    )}
                </div>
            </div>

            {/* Card 3: Totais por Forma de Pagamento (Mensal - Entradas E Saídas) - MAIS COMPACTO */}
            <div style={cardStyle}>
                <h3 style={titleStyle}>Pagamentos ({currentMonth})</h3>
                <div style={paymentSectionScroll}>
                    {" "}
                    {/* Adicionado para scroll vertical se necessário */}
                    {paymentTypes &&
                        paymentTypes.filter((type) => type !== "Todos").length > 0 ? (
                        paymentTypes
                            .filter((type) => type !== "Todos")
                            .map((type) => (
                                <div key={type} style={paymentRowStyleCompact}>
                                    <span style={textStyleCompact}>{type}:</span>
                                    <div style={paymentValueGroup}>
                                        <span
                                            style={{
                                                ...valueStyleMicro,
                                                ...blurStyle,
                                                color: "#2ecc71",
                                            }}
                                        >
                                            {formatCurrency(totals?.paymentMethodsIncome[type])}
                                        </span>
                                        <span
                                            style={{
                                                ...valueStyleMicro,
                                                ...blurStyle,
                                                color: "#e74c3c",
                                            }}
                                        >
                                            {formatCurrency(totals?.paymentMethodsExpense[type])}
                                        </span>
                                    </div>
                                </div>
                            ))
                    ) : (
                        <p style={emptyTextStyle}>Nenhum dado disponível para este mês.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- ESTILOS ---

const cardStyle = {
    backgroundColor: "white",
    padding: "8px", // Reduzido o padding interno do card
    borderRadius: "8px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    display: "flex",
    flexDirection: "column",
    gap: "6px", // Reduzido o gap entre os elementos internos
    height: "fit-content",
    minHeight: "130px", // Altura mínima um pouco menor
};

const titleStyle = {
    fontSize: "14px", // Tamanho do título um pouco menor
    margin: "0 0 6px 0", // Margem ajustada
    color: "#333",
    textAlign: "center",
    fontWeight: "600",
};

const periodSectionStyle = {
    border: "1px solid #eee", // Borda mais suave
    borderRadius: "4px",
    padding: "6px", // Padding menor
    backgroundColor: "#fdfdfd",
    flexShrink: 0,
};

const labelStyle = {
    fontSize: "11px", // Título da seção (Diário, Mensal, Anual) menor
    color: "#777",
    margin: "0 0 1px 0", // Margem menor
    fontWeight: "bold",
};

const compactValueRow = {
    display: "flex",
    justifyContent: "space-between",
    gap: "4px", // Espaçamento entre os valores (E: S: Saldo:)
    flexWrap: "wrap",
};

const valueStyleMicro = {
    fontSize: "12px", // Tamanho da fonte dos valores bem pequeno
    fontWeight: "bold",
    margin: 0,
    whiteSpace: "nowrap",
};

const paymentRowStyleCompact = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid #f0f0f0",
    paddingBottom: "3px", // Padding menor
    marginBottom: "3px", // Margem menor
};

const paymentValueGroup = {
    display: "flex",
    gap: "6px", // Espaçamento entre os valores de Entrada e Saída
    flexShrink: 0,
};

const textStyleCompact = {
    fontSize: "11px", // Tamanho da fonte do tipo de pagamento
    color: "#555",
    fontWeight: "600",
    whiteSpace: "nowrap",
};

const emptyTextStyle = {
    fontSize: "11px", // Tamanho da fonte menor
    color: "#999",
    textAlign: "center",
    padding: "8px 0", // Padding para centralizar
};

const paymentSectionScroll = {
    maxHeight: "160px", // Altura máxima antes de aparecer a barra de rolagem um pouco menor
    overflowY: "auto",
    paddingRight: "5px",
};

export default SummaryCards;
